import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../redux/store';
import {
    setCurrentList,
    addItem,
    removeItem,
    removeItemFromList,
    clearCurrentList,
    setSavedLists,
    addSavedList,
    updateSavedList,
    deleteSavedList,
    loadList,
    setListActive,
    SavedShoppingList
} from '../redux/shoppingListSlice';
import { shoppingListApi } from '../api';

export const useShoppingListRedux = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { currentList, savedLists } = useSelector((state: RootState) => state.shoppingList);

    // Load current shopping list from localStorage
    const loadCurrentList = useCallback(() => {
        try {
            const currentListData = localStorage.getItem('currentShoppingList');
            if (currentListData) {
                const parsed = JSON.parse(currentListData);
                dispatch(setCurrentList(parsed));
            }
        } catch (error) {
            console.error('Error loading current shopping list:', error);
        }
    }, [dispatch]);

    // Load saved lists from API or localStorage (fallback)
    const loadSavedLists = useCallback(async () => {
        try {
            // Try to load from API first
            const response = await shoppingListApi.getShoppingLists();
            if (response.lists && response.lists.length > 0) {
                // Fetch items for each list (GET /shopping-list/{id}/items returns array; we aggregate by product_id for count/total)
                const listsWithItems = await Promise.all(
                    response.lists.map(async (list) => {
                        try {
                            const items = await shoppingListApi.getShoppingListItems(list.id);
                            return { ...list, items };
                        } catch {
                            return { ...list, items: list.items || {} };
                        }
                    })
                );
                dispatch(setSavedLists(listsWithItems));
            } else {
                // Fallback to localStorage if API returns empty
            const saved = localStorage.getItem('shoppingLists');
            if (saved) {
                const parsed = JSON.parse(saved);
                    if (Array.isArray(parsed)) {
                const listsWithStringDates = parsed.map((list: any) => {
                    const items = list.items && typeof list.items === 'object' ? list.items : {};
                    return {
                        id: list.id || Date.now().toString(),
                        name: list.name || 'Unnamed List',
                                items: items,
                        createdAt: typeof list.createdAt === 'string' ? list.createdAt : (list.createdAt ? new Date(list.createdAt).toISOString() : new Date().toISOString()),
                        updatedAt: typeof list.updatedAt === 'string' ? list.updatedAt : (list.updatedAt ? new Date(list.updatedAt).toISOString() : new Date().toISOString()),
                                active: list.active === true
                    };
                });
                dispatch(setSavedLists(listsWithStringDates));
                    }
                }
            }
        } catch (error) {
            console.error('Error loading saved lists from API, falling back to localStorage:', error);
            // Fallback to localStorage on error
            try {
                const saved = localStorage.getItem('shoppingLists');
                if (saved) {
                    const parsed = JSON.parse(saved);
                    if (Array.isArray(parsed)) {
                        const listsWithStringDates = parsed.map((list: any) => {
                            const items = list.items && typeof list.items === 'object' ? list.items : {};
                            return {
                                id: list.id || Date.now().toString(),
                                name: list.name || 'Unnamed List',
                                items: items,
                                createdAt: typeof list.createdAt === 'string' ? list.createdAt : (list.createdAt ? new Date(list.createdAt).toISOString() : new Date().toISOString()),
                                updatedAt: typeof list.updatedAt === 'string' ? list.updatedAt : (list.updatedAt ? new Date(list.updatedAt).toISOString() : new Date().toISOString()),
                                active: list.active === true
                            };
                        });
                        dispatch(setSavedLists(listsWithStringDates));
                    }
                }
            } catch (localError) {
                console.error('Error loading saved lists from localStorage:', localError);
            }
        }
    }, [dispatch]);

    // Initialize on mount
    useEffect(() => {
        loadCurrentList();
        loadSavedLists();
    }, [loadCurrentList, loadSavedLists]);

    // Save current shopping list to localStorage whenever it changes
    useEffect(() => {
        if (Object.keys(currentList).length > 0) {
            localStorage.setItem('currentShoppingList', JSON.stringify(currentList));
        } else {
            localStorage.removeItem('currentShoppingList');
        }
    }, [currentList]);

    // Save saved lists to localStorage whenever they change
    useEffect(() => {
        if (savedLists && savedLists.length > 0) {
            localStorage.setItem('shoppingLists', JSON.stringify(savedLists));
        } else {
            // If empty, still save empty array to clear any old data
            localStorage.setItem('shoppingLists', JSON.stringify([]));
        }
    }, [savedLists]);

    const addItemToCart = useCallback((id: string) => {
        dispatch(addItem(id));
    }, [dispatch]);

    const removeItemFromCart = useCallback((id: string) => {
        dispatch(removeItem(id));
    }, [dispatch]);

    const removeItemFromListCompletely = useCallback((id: string) => {
        dispatch(removeItemFromList(id));
    }, [dispatch]);

    const clearCurrentListCompletely = useCallback(() => {
        dispatch(clearCurrentList());
        localStorage.removeItem('currentShoppingList');
    }, [dispatch]);

    const saveList = useCallback(async (name: string, editingId?: string, itemsOverride?: Record<string, number>) => {
        const items: Record<string, number> = itemsOverride !== undefined
            ? itemsOverride
            : editingId
                ? (savedLists.find(l => l.id === editingId)?.items ?? { ...currentList })
                : { ...currentList };

        try {
            if (editingId) {
                const response = await shoppingListApi.updateShoppingList(editingId, {
                    name: name.trim(),
                    items,
                });
                dispatch(updateSavedList(response.list));
            } else {
                const response = await shoppingListApi.createShoppingList({
                    name: name.trim(),
                    items,
                    active: false,
                });
                dispatch(addSavedList(response.list));
            }
        } catch (error) {
            console.error('Failed to save shopping list to backend:', error);
            throw error;
        }
    }, [currentList, savedLists, dispatch]);

    const loadListFromSaved = useCallback((list: SavedShoppingList) => {
        dispatch(loadList(list));
    }, [dispatch]);

    const deleteListFromSaved = useCallback(async (listId: string) => {
        try {
            await shoppingListApi.deleteShoppingList(listId);
            dispatch(deleteSavedList(listId));
        } catch (error) {
            console.error('Failed to delete shopping list on backend:', error);
            throw error;
        }
    }, [dispatch]);

    const editListFromSaved = useCallback((list: SavedShoppingList) => {
        return {
            name: list.name,
            id: list.id
        };
    }, []);

    const addSavedListDirectly = useCallback((list: SavedShoppingList) => {
        dispatch(addSavedList(list));
    }, [dispatch]);

    const updateSavedListDirectly = useCallback((list: SavedShoppingList) => {
        dispatch(updateSavedList(list));
    }, [dispatch]);

    const setListActiveStatus = useCallback(async (listId: string, active: boolean) => {
        try {
            const response = await shoppingListApi.setActiveShoppingList(listId, active);
            dispatch(updateSavedList(response.list));
            dispatch(setListActive({ listId, active }));

            // Persist the active shopping list id so other pages (like ShoppingCartPage)
            // can reliably sync item changes with the backend.
            if (active) {
                localStorage.setItem('activeShoppingListId', listId);
            } else {
                const currentActiveId = localStorage.getItem('activeShoppingListId');
                if (currentActiveId === listId) {
                    localStorage.removeItem('activeShoppingListId');
                }
            }
        } catch (error) {
            console.error('Failed to update list active status:', error);
            throw error;
        }
    }, [dispatch]);

    // Helper function to get formatted date for display
    const getFormattedDate = useCallback((dateString: string) => {
        return new Date(dateString).toLocaleDateString();
    }, []);

    return {
        shoppingList: currentList,
        savedLists,
        addItem: addItemToCart,
        removeItem: removeItemFromCart,
        removeItemFromList: removeItemFromListCompletely,
        clearCurrentList: clearCurrentListCompletely,
        saveList,
        loadList: loadListFromSaved,
        deleteList: deleteListFromSaved,
        editList: editListFromSaved,
        addSavedList: addSavedListDirectly,
        updateSavedList: updateSavedListDirectly,
        setListActive: setListActiveStatus,
        loadCurrentList,
        loadSavedLists,
        getFormattedDate
    };
};
